import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, MenuItem, Button,
  Stepper, Step, StepLabel, Alert, Divider, Dialog, DialogContent,
  DialogActions, FormControlLabel, Checkbox, InputAdornment, Tabs, Tab,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileUpload from '../../components/common/FileUpload';

const agentSteps = ['Personal Information', 'Business Details', 'Documents', 'Review'];

export default function ReferralRegistration() {
  const [registrationType, setRegistrationType] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [form, setForm] = useState({
    fullName: '', icNumber: '', email: '', phone: '', address: '',
    state: '', companyName: '', companyRegNo: '', bankName: '', bankAccount: '',
    experience: '', productInterest: [], agree: false,
  });

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const renderUserRegistration = () => (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Register as Referral User</Typography>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Register to start referring friends and earn token rewards! Your phone number will be your referral code.
        </Alert>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Full Name *" value={form.fullName} onChange={update('fullName')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="IC Number *" value={form.icNumber} onChange={update('icNumber')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Email *" value={form.email} onChange={update('email')} type="email" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Phone Number *" value={form.phone} onChange={update('phone')}
              InputProps={{ startAdornment: <InputAdornment position="start">+60</InputAdornment> }}
              helperText="This will be your referral code"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} />}
              label={<Typography variant="body2">I agree to the terms and conditions of the iMoney Referral Program</Typography>}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={() => setShowSuccess(true)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, backgroundColor: '#1B365D', px: 4 }}>
              Register
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderAgentStep0 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Full Name *" value={form.fullName} onChange={update('fullName')} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="IC Number *" value={form.icNumber} onChange={update('icNumber')} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Email *" value={form.email} onChange={update('email')} type="email" />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Phone Number *" value={form.phone} onChange={update('phone')}
          InputProps={{ startAdornment: <InputAdornment position="start">+60</InputAdornment> }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth label="Address" value={form.address} onChange={update('address')} multiline rows={2} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="State *" value={form.state} onChange={update('state')}>
          {['Kuala Lumpur', 'Selangor', 'Johor', 'Penang', 'Perak', 'Sabah', 'Sarawak', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perlis', 'Terengganu'].map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );

  const renderAgentStep1 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Company Name (if applicable)" value={form.companyName} onChange={update('companyName')} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Company Registration No." value={form.companyRegNo} onChange={update('companyRegNo')} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="Years of Experience in Finance" value={form.experience} onChange={update('experience')}>
          <MenuItem value="0-1">Less than 1 year</MenuItem>
          <MenuItem value="1-3">1-3 years</MenuItem>
          <MenuItem value="3-5">3-5 years</MenuItem>
          <MenuItem value="5+">5+ years</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Bank Name (for commission)" value={form.bankName} onChange={update('bankName')} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Bank Account Number" value={form.bankAccount} onChange={update('bankAccount')} />
      </Grid>
    </Grid>
  );

  const renderAgentStep2 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <FileUpload label="IC / MyKad (Front & Back) *" maxFiles={2} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUpload label="SSM Registration (if company)" maxFiles={1} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUpload label="Resume / CV" maxFiles={1} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUpload label="Professional Certifications" maxFiles={3} />
      </Grid>
    </Grid>
  );

  const renderAgentStep3 = () => (
    <Box>
      <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
        Please review your information. Your registration will be reviewed by our team within 3 business days.
      </Alert>
      <Grid container spacing={2}>
        {[
          ['Full Name', form.fullName], ['IC Number', form.icNumber],
          ['Email', form.email], ['Phone', form.phone],
          ['Company', form.companyName || 'Individual'], ['Experience', form.experience],
        ].map(([label, val]) => (
          <Grid item xs={6} key={label}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{val || '-'}</Typography>
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ my: 3 }} />
      <FormControlLabel
        control={<Checkbox checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} />}
        label={<Typography variant="body2">I confirm all information provided is accurate and agree to iMoney's Agent Terms & Conditions</Typography>}
      />
    </Box>
  );

  const agentStepContent = [renderAgentStep0, renderAgentStep1, renderAgentStep2, renderAgentStep3];

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D', mb: 1 }}>Join Us</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Register as a referral user or apply to become an iMoney agent
      </Typography>

      <Tabs value={registrationType} onChange={(_, v) => { setRegistrationType(v); setActiveStep(0); }} sx={{ mb: 3 }}>
        <Tab icon={<PersonAddIcon />} iconPosition="start" label="Referral User" sx={{ textTransform: 'none' }} />
        <Tab icon={<BusinessIcon />} iconPosition="start" label="Individual Agent" sx={{ textTransform: 'none' }} />
      </Tabs>

      {registrationType === 0 ? renderUserRegistration() : (
        <>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {agentSteps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                {agentSteps[activeStep]}
              </Typography>
              {agentStepContent[activeStep]()}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)} sx={{ textTransform: 'none' }}>
              Back
            </Button>
            <Button variant="contained"
              onClick={() => {
                if (activeStep === agentSteps.length - 1) setShowSuccess(true);
                else setActiveStep((s) => s + 1);
              }}
              sx={{ textTransform: 'none', borderRadius: 2, px: 4, backgroundColor: '#1B365D' }}
            >
              {activeStep === agentSteps.length - 1 ? 'Submit Registration' : 'Next'}
            </Button>
          </Box>
        </>
      )}

      <Dialog open={showSuccess} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: '#00B894', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Registration Submitted!</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {registrationType === 0
              ? 'Welcome! Your referral account is now active. Share your phone number as your referral code.'
              : 'Your agent registration is under review. You will be notified once approved.'}
          </Typography>
          {registrationType === 0 && form.phone && (
            <Box sx={{ backgroundColor: '#F5F7FA', borderRadius: 2, p: 2, display: 'inline-block' }}>
              <Typography variant="caption" color="text.secondary">Your Referral Code</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1B365D' }}>+60{form.phone}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" onClick={() => setShowSuccess(false)}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: '#1B365D' }}>
            Go to Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
